import { CdkDropList } from '@angular/cdk/drag-drop';
import { Subscription, ReplaySubject } from 'rxjs';
import { Injectable } from '@angular/core';
import { EntityStoreService } from '../../../../services/entity-store.service';
import { IAction, IWidgetGroupTag } from '../redux/timeline-data.model';
import { TranslateService } from '@ngx-translate/core';
import { InteractivityWidgetDefinitionService } from './interactivity-widget-definition.service';
import { InteractivityEditorService } from './interactivity-editor.service';
import { WidgetType } from '../enums/WidgetType';
import { TimeService } from '../../../../services/time.service';

@Injectable()
export class InteractivityActionService {
	entityLabelsByExtendedId: any = {};
	private _actionDropsites: CdkDropList[];
	private _actionDropsitesSubject: ReplaySubject<CdkDropList[]>;
	private _widgetPropOptionsByKey = {};
	private _widgetMethodOptionsByKey = {};
	private _catMap: any = this.makeCategoryMap();
	private _selectedActions: string[] = [];
	private subscriptions: Subscription;

	constructor(
		private translate: TranslateService,
		private interactivityWidgetDefinitionService: InteractivityWidgetDefinitionService,
		private interactivityEditorService: InteractivityEditorService,
		private timeService: TimeService,
		private entityStoreService: EntityStoreService
	) {
		this._actionDropsitesSubject = new ReplaySubject<CdkDropList[]>(1);
		this._actionDropsites = [];

		this.getWidgetPropOptions().map(option => option && (this._widgetPropOptionsByKey[option.value] = option));
		this.getWidgetMethodOptions().map(option => option && (this._widgetMethodOptionsByKey[option.value] = option));

		this.subscriptions = new Subscription();
		this.subscriptions.add(this.interactivityEditorService.widgetsSubject.subscribe(widgets => {
			for (let i = 0, l = widgets.length; i < l; i++) {
				if (widgets[i] && widgets[i].id) { // avoid empty string key
					this.entityLabelsByExtendedId['Widget:' + widgets[i].id] = widgets[i].label;
				}
			}
		}));

		this.subscriptions.add(
			this.interactivityEditorService.groupTagSubject.subscribe((groupTags: IWidgetGroupTag[]) => {
				if (groupTags && groupTags.length) {
					groupTags.forEach((groupTag) => {
						this.entityLabelsByExtendedId['Group:' + groupTag.id] = groupTag.label;
					});
				}
			}));
	}

	get actionDropsitesSubject(): ReplaySubject<CdkDropList[]> {
		return this._actionDropsitesSubject;
	}

	getEntityLabelForExtendedId(xid: string): string {
		let label: string;
		if (this.entityLabelsByExtendedId[xid] !== undefined) {
			label = this.entityLabelsByExtendedId[xid];
		} else {
			const xid_parts: string[] = xid && xid.split(':');
			const entity = this.entityStoreService.getEntity(xid_parts[0].toLowerCase(), xid_parts[1]);
			if (entity) {
				label = this.entityLabelsByExtendedId[xid] = entity.title;
			}
		}
		return label;
	}

	public clearActionDropSites(): void {
		this._actionDropsites = [];
	}

	public addActionDropSites(dropSItes: CdkDropList[]): void {
		dropSItes.forEach(dropSite => {
			if (this._actionDropsites.indexOf(dropSite) === -1) {
				this._actionDropsites.push(dropSite);
			}
		});
		this.dispatchChanges();
	}

	selectAction(actionUid: string) {
		if (this._selectedActions.indexOf(actionUid) === -1) {
			this._selectedActions.push(actionUid);
		}
	}

	deselectAction(actionUid: string) {
		const i = this._selectedActions.indexOf(actionUid);
		if (i !== -1) {
			this._selectedActions.splice(i, 1);
		}
	}

	deselectAllActions() {
		this._selectedActions = [];
	}

	isActionSelected(actionUid: string) {
		return this._selectedActions.indexOf(actionUid) !== -1;
	}

	getCategoryActionMap() {
		const actionCategories = {};
		for (const entityName in this._catMap) {
			if (entityName && this._catMap[entityName]) {
				const entity = this._catMap[entityName];
				for (const commandName in entity) {
					if (commandName === '__NONE') { // no entity specified
						const cat = entity.__NONE.actionCat || entityName;
						actionCategories[cat] = actionCategories[cat] || [];
						actionCategories[cat].push({
							entity: entityName,
							command: ''
						});
					} else if (commandName && entity[commandName]) {
						const cat = entity[commandName].actionCat || entityName;
						actionCategories[cat] = actionCategories[cat] || [];
						actionCategories[cat].push({
							entity: entityName,
							command: commandName
						});
					}
				}
			}
		}
		return actionCategories;
	}

	getCategoryForAction(action: IAction) {
		let cat = action.entity;
		if (this._catMap && this._catMap[action.entity]) {
			const entityObj = this._catMap[action.entity];
			const command = action.command || '__NONE';
			if (entityObj && entityObj[command] && entityObj[command].actionCat) {
				cat = entityObj[command].actionCat;
			} else if (entityObj.__ANY && entityObj.__ANY.actionCat) {
				cat = entityObj.__ANY.actionCat;
			}
		}
		return cat;
	}

	getShortTextForAction(action: IAction) {
		let shortText = '';
		if (this._catMap && this._catMap[action.entity]) {
			const entityObj = this._catMap[action.entity];
			const command = action.command || '__NONE';
			if (entityObj && entityObj[command] && entityObj[command].shortText) {
				shortText = entityObj[command].shortText;
			} else if (entityObj.__ANY && entityObj.__ANY.shortText) {
				shortText = entityObj.__ANY.shortText;
			} else if (entityObj.shortText) {
				shortText = entityObj.shortText;
			}
		}
		return shortText;
	}

	getClosedTextForAction(action: IAction) {
		let closedText = { prop: '', val: '', command: '' };
		if (this._catMap && this._catMap[action.entity]) {
			const entityObj = this._catMap[action.entity];
			const command = action.command || '__NONE';
			if (entityObj && entityObj[command] && entityObj[command].closedText) {
				closedText = entityObj[command].closedText;
			} else if (entityObj.__ANY && entityObj.__ANY.closedText) {
				closedText = entityObj.__ANY.closedText;
			} else if (entityObj.closedText) {
				closedText = entityObj.closedText;
			}
		}
		return closedText;
	}

	getPropertyValueType(property: string, forFieldRenderer = false): string {
		let valueType = null;

		const strippedProperty = property.replace('setProperty:', '');

		switch (strippedProperty) {
			case 'xpos':
			case 'ypos':
			case 'width':
			case 'height':
			case 'delay':
			case 'repeatCount':
			case 'cornerradius':
			case 'borderthickness':
			case 'transition':
			case 'customTransition':
				valueType = 'number';
				break;
			case 'seek':
				valueType = 'timeCodeSelector';
				break;

			case 'clipid':
				valueType = 'entitySelector';
				break;

			case 'imageid':
			case 'imageid_mouseover':
			case 'imageid_mousedown':
			case 'imageid_toggle':
				valueType = 'entitySelector:image';
				break;

			case 'alpha':
			case 'background_alpha':
				valueType = forFieldRenderer ? 'number' : '100slider';
				break;

			case 'volume':
			case 'volumeTransition':
				valueType = 'slider';
				break;

			case 'loop':
			case 'showControls':
			case 'ignoreTimelineShow':
			case 'ignoreTimelineHide':
			case 'preload':
			case 'useAsKPI':
				valueType = 'checkbox';
				break;

			case 'value':
			case 'placeHolder':
			case 'formElementName':
			case 'formId':
			case 'validation':
			case 'text':
			case 'setvalue':
			case 'customconfig':
				valueType = 'text';
				break;
			case 'textFormat/textFormat/align':
				valueType = forFieldRenderer ? 'align' : 'text';
				break;
			case 'textFormat/textFormat/verticalalign':
				valueType = forFieldRenderer ? 'verticalalign' : 'text';
				break;

			case 'textFormat/textFormat/bold':
			case 'textFormat/textFormat/italic':
			case 'textFormat/textFormat/underline':
				// valueType = 'checkbox';
				valueType = forFieldRenderer ? 'togglebutton' : 'checkbox';
				break;
			case 'cssclass':
			case 'textFormat/textFormat/margin':
			case 'textFormat/textFormat/fontsize':
				valueType = 'string';
				break;
			case 'textFormat/textFormat/embeddedfont':
			case 'textFormat/textFormat/font':
				valueType = forFieldRenderer ? 'font' : 'entitySelector:font';
				break;

			// TODO add a color picker
			case 'textFormat/textFormat/color':
			case 'backgroundcolor':
			case 'bordercolor':
			case 'color':
				valueType = forFieldRenderer ? 'color' : 'text';
				break;
		}
		return valueType;
	}

	public getWidgetPropOptionsForType(type: string): any[] {
		let widgetPropOptions: any[] = [];
		const widgetDefaults = this.interactivityWidgetDefinitionService.getWidgetDefaultsForType(type);
		const flatDefaults: any = Object.assign({}, this.flattenConfig_recursive(widgetDefaults));
		if (flatDefaults) {
			widgetPropOptions = Object.keys(flatDefaults).filter((key: string) => !key.match(/^on/)).map((key: string) => {
				let option: any = { value: key, label: key[0].toUpperCase() + key.slice(1) }; // default
				if (this._widgetPropOptionsByKey[key] !== undefined) {
					option = this._widgetPropOptionsByKey[key];
				}
				return option;
			});
		}

		return widgetPropOptions;
	}

	/**
	 * Get widget control options for a specific widget type
	 *   ("control" = property or method)
	 */
	public getWidgetControlOptionsForType(type: string): any[] {
		const widgetControlOptions: any[] = [];

		const widgetDefaults = this.interactivityWidgetDefinitionService.getWidgetDefaultsForType(type);
		const flatDefaults: any = Object.assign({}, this.flattenConfig_recursive(widgetDefaults));

		const hiPrioMethods: any = {};
		const loPrioMethods: any = {};
		switch (type) {
			case WidgetType.Video:
				hiPrioMethods.show = null;
				hiPrioMethods.hide = null;
				loPrioMethods.transition = null;
				loPrioMethods.customTransition = null;
			// eslint-disable-next-line no-fallthrough
			case WidgetType.Audio:
				loPrioMethods.pause = null;
				loPrioMethods.play = null;
				loPrioMethods.seek = null;
				loPrioMethods.mute = null;
				loPrioMethods.unmute = null;
				loPrioMethods.volumetransition = null;
				break;

			case WidgetType.Timer:
				loPrioMethods.start = null;
				loPrioMethods.stop = null;
				loPrioMethods.reset = null;
				break;

			case WidgetType.Image:
				hiPrioMethods.show = null;
				hiPrioMethods.hide = null;
				loPrioMethods.transition = null;
				loPrioMethods.customTransition = null;
				loPrioMethods.toggleImage = null;
				break;

			case WidgetType.FormTextArea:
			case WidgetType.FormTextField:
			case WidgetType.FormSelect:
				hiPrioMethods.show = null;
				hiPrioMethods.hide = null;
				loPrioMethods.transition = null;
				loPrioMethods.customTransition = null;
				loPrioMethods.setValue = null;
				break;

			case WidgetType.Group:
				hiPrioMethods.show = null;
				hiPrioMethods.hide = null;
				loPrioMethods.transition = null;
				break;

			default:
				hiPrioMethods.show = null;
				hiPrioMethods.hide = null;
				loPrioMethods.transition = null;
				loPrioMethods.customTransition = null;
		}
		// all:
		loPrioMethods.restoreInitialConfig = null;

		if (hiPrioMethods) {
			Object.keys(hiPrioMethods).map((key: string) => {
				let option: any = { value: key, label: key[0].toUpperCase() + key.slice(1) }; // default
				if (this._widgetMethodOptionsByKey[key] !== undefined) {
					option = this._widgetMethodOptionsByKey[key];
				}
				widgetControlOptions.push(option);
			});
		}
		if (flatDefaults) {
			Object.keys(flatDefaults).filter((key: string) => !key.match(/^on/)).map((key: string) => {
				let option: any = { value: 'setProperty:' + key, label: key[0].toUpperCase() + key.slice(1) }; // default
				if (this._widgetPropOptionsByKey[key] !== undefined) {
					option = { ...this._widgetPropOptionsByKey[key] };
					option.value = 'setProperty:' + key;
				}
				widgetControlOptions.push(option);
			});
		}
		if (loPrioMethods) {
			Object.keys(loPrioMethods).map((key: string) => {
				let option: any = { value: key, label: key[0].toUpperCase() + key.slice(1) }; // default
				if (this._widgetMethodOptionsByKey[key] !== undefined) {
					option = this._widgetMethodOptionsByKey[key];
				}
				widgetControlOptions.push(option);
			});
		}

		return widgetControlOptions;
	}

	public getWidgetPropOptions() {
		return [
			{
				category: 'Position',
				value: 'xpos',
				label: 'X position',
				aliases: ['Left',
					'X coordinate',
					'X co-ordinate',
					'X-position',
					'X-coordinate']
			},
			{
				category: 'Position',
				value: 'ypos',
				label: 'Y position',
				aliases: ['Top',
					'Y coordinate',
					'Y co-ordinate',
					'Y-position',
					'Y-coordinate']
			},
			{
				category: 'Size',
				value: 'width',
				label: 'Width',
				aliases: ['Horizontal size']
			},
			{
				category: 'Size',
				value: 'height',
				label: 'Height',
				aliases: ['Vertical size']
			},
			{
				category: 'Color',
				icon: '',
				value: 'color',
				label: 'Fill color',
				aliases: ['Background color',
					'Fill colour',
					'Background colour']
			},
			{
				category: 'Border',
				icon: '',
				value: 'cornerradius',
				label: 'Corner radius',
				aliases: ['Corner roundness',
					'Border radius',
					'Border roundness',
					'Edge radius',
					'Edge roundness',
					'Stroke radius',
					'Stroke roundness']
			},
			{
				category: 'Border',
				icon: '',
				value: 'borderthickness',
				label: 'Border width',
				aliases: ['Stroke width',
					'Stroke thickness',
					'Border thickness',
					'Edge thickness',
					'Edge width']
			},
			{
				category: 'Border, Color',
				icon: '',
				value: 'bordercolor',
				label: 'Border color',
				aliases: ['Stroke color',
					'Edge color',
					'Stroke colour',
					'Border colour',
					'Edge colour']
			},
			{
				category: 'Color',
				value: 'alpha',
				label: 'Opacity',
				aliases: ['Alpha',
					'Transparency']
			},
			{
				category: 'Other',
				value: 'showControls',
				label: 'Show controls',
				aliases: []
			},
			{
				category: 'Media',
				value: 'clipid',
				label: 'Clip ID',
				aliases: ['Media clip',
					'Video clip',
					'Movie']
			},
			{
				category: 'Image',
				value: 'imageid',
				label: 'Image ID',
				aliases: ['Picture ID',
					'Photo ID']
			},
			{
				category: 'Image',
				value: 'imageid_mouseover',
				label: 'Mouseover image ID',
				aliases: ['Mouse-over image ID',
					'Mouse over image ID']
			},
			{
				category: 'Image',
				value: 'imageid_mousedown',
				label: 'Mousedown image ID',
				aliases: ['Mouse-down image ID',
					'Mouse down image ID']
			},
			{
				category: 'Image',
				value: 'imageid_toggle',
				label: 'Toggled image ID',
				aliases: ['Toggle image ID']
			},
			{
				category: 'Image',
				value: 'toggled',
				label: 'Toggled',
				aliases: ['Toggled', 'Switched']
			},
			{
				category: 'Text',
				icon: '',
				value: 'text',
				label: 'Text content',
				aliases: ['Contents']
			},
			{
				category: 'Text',
				value: 'value',
				label: 'Value',
				aliases: []
			},
			{
				category: 'Text',
				icon: '',
				value: 'textFormat/textFormat/bold',
				label: 'Text bold',
				aliases: ['Font weight bold', 'Bold']
			},
			{
				category: 'Text',
				icon: '',
				value: 'textFormat/textFormat/italic',
				label: 'Text italic',
				aliases: ['Font style italic', 'Italic']
			},
			{
				category: 'Text',
				icon: '',
				value: 'textFormat/textFormat/underline',
				label: 'Text underlined',
				aliases: ['Text decoration underline', 'Underline']
			},
			{
				category: 'Text',
				icon: '',
				value: 'textFormat/textFormat/margin',
				label: 'Text margin',
				aliases: ['Margin']
			},
			{
				category: 'Text',
				icon: '',
				value: 'textFormat/textFormat/fontsize',
				label: 'Text size',
				aliases: ['Font size']
			},
			{
				category: 'Text',
				icon: '',
				value: 'textFormat/textFormat/align',
				label: 'Text align',
				aliases: ['Align']
			},
			{
				category: 'Text',
				icon: '',
				value: 'textFormat/textFormat/color',
				label: 'Text color',
				aliases: ['Font color']
			},
			{
				category: 'Text',
				icon: '',
				value: 'textFormat/textFormat/font',
				label: 'Text font',
				aliases: ['Font family', 'Font face', 'Typeface', 'Font']
			},
			{
				category: 'Text, Color',
				icon: '',
				value: 'background',
				label: 'Show background',
				aliases: ['Show fill']
			},
			{
				category: 'Text, Color',
				icon: '',
				value: 'backgroundcolor',
				label: 'Fill color',
				aliases: ['Background color',
					'Fill colour',
					'Background colour']
			},
			{
				category: 'Text',
				icon: '',
				value: 'background_alpha',
				label: 'Fill alpha',
				aliases: ['Background alpha', 'Background']
			},
			{
				category: 'Text',
				value: 'toppadding',
				label: 'Top padding',
				aliases: ['Upper padding',
					'Top margin',
					'Upper margin']
			},
			{
				category: 'Text',
				value: 'bottompadding',
				label: 'Bottom padding',
				aliases: ['Lower padding',
					'Bottom margin',
					'Lower margin']
			},
			{
				category: 'Other',
				value: 'placeHolder',
				label: 'Placeholder',
				aliases: []
			},
			{
				category: 'Form',
				value: 'formElementName',
				label: 'Form element name',
				aliases: ['Form object name']
			},
			{
				category: 'Form',
				value: 'formId',
				label: 'Form ID',
				aliases: []
			},
			{
				category: 'Form',
				value: 'validation',
				label: 'Validation',
				aliases: []
			},
			{
				category: 'Media',
				value: 'volume',
				label: 'Volume',
				aliases: ['Audio volume']
			},
			{
				category: 'Media',
				value: 'loop',
				label: 'Repeat',
				aliases: ['Loop',
					'Replay']
			},
			{
				category: 'Media',
				value: 'delay',
				label: 'Delay',
				aliases: []
			},
			{
				category: 'Media',
				value: 'repeatCount',
				label: 'Repeat count',
				aliases: ['Loop count',
					'Replay count']
			},
			{
				category: 'Other',
				value: 'title',
				label: 'Chapter title',
				aliases: []
			},
			{
				category: 'Other',
				value: 'description',
				label: 'Chapter description',
				aliases: []
			},
			{
				category: 'Other',
				value: 'ignoreTimelineShow',
				label: 'Ignore start time',
				aliases: ['Disable start time',
					'Ignore in point',
					'Disable in point']
			},
			{
				category: 'Other',
				value: 'ignoreTimelineHide',
				label: 'Ignore end time',
				aliases: ['Disable end time',
					'Ignore out point',
					'Disable our point']
			},
			{
				category: 'Other',
				value: 'showOnMouseHover',
				label: 'Show on mouseover',
				aliases: []
			},
			{
				category: 'Other',
				value: 'cssclass',
				label: 'CSS class',
				aliases: ['Style class']
			},
			{
				category: 'Other',
				value: 'useAsKPI',
				label: 'KPI',
				aliases: ['Metric']
			},
			{
				category: 'Other',
				value: 'customJavascriptUrl',
				label: 'Javascript URL',
				aliases: ['Custom javascript URL']
			},
			{
				category: 'Other',
				value: 'widgetClassName',
				label: 'Class name',
				aliases: ['CSS class']
			},
			{
				category: 'Other',
				value: 'customconfig',
				label: 'Custom configuration',
				aliases: []
			},
			{
				category: 'Other',
				value: 'appid',
				label: 'App ID',
				aliases: ['Component ID']
			},
			{
				category: 'Other',
				value: 'command:customTransition',
				label: 'Custom transition',
				aliases: []
			},
			{
				category: 'Other',
				value: 'command:show',
				label: 'Show',
				aliases: []
			},
			{
				category: 'Other',
				value: 'command:hide',
				label: 'Hide',
				aliases: []
			},
			{
				category: 'Other',
				value: 'command:start',
				label: 'Start',
				aliases: ['Resume']
			},
			{
				category: 'Other',
				value: 'command:stop',
				label: 'Stop',
				aliases: []
			},
			{
				category: 'Other',
				value: 'command:reset',
				label: 'Reset',
				aliases: []
			},
			{
				category: 'Other',
				value: 'command:play',
				label: 'Play',
				aliases: ['Resume']
			},
			{
				category: 'Other',
				value: 'command:pause',
				label: 'Pause',
				aliases: []
			},
			{
				category: 'Other',
				value: 'command:mute',
				label: 'Mute',
				aliases: []
			},
			{
				category: 'Other',
				value: 'command:unmute',
				label: 'Unmute',
				aliases: []
			},
			{
				category: 'Other',
				value: 'command:transition',
				label: 'Transition',
				aliases: []
			},
			{
				category: 'Other',
				value: 'volumeTransition',
				label: 'Volume transition',
				aliases: []
			},
			{
				category: 'Other',
				value: 'restoreInitialConfig',
				label: 'Restore initial values',
				aliases: ['Reset', 'Restore', 'Revert', 'Initial']
			},
			{
				category: 'Stats',
				value: 'stats_shows',
				label: 'Widget shows',
				aliases: []
			},
			{
				category: 'Stats',
				value: 'stats_clicks',
				label: 'Widget clicks',
				aliases: []
			}
		];
	}

	public getWidgetMethodOptions() {
		return [
			{
				category: 'Stage',
				value: 'show',
				label: 'Show',
				aliases: ['visible', 'displayed']
			},
			{
				category: 'Stage',
				value: 'hide',
				label: 'Hide',
				aliases: ['invisible', 'hidden']
			},
			{
				category: 'Stage',
				value: 'transition',
				label: 'Transition',
				aliases: ['tween']
			},
			{
				category: 'Stage',
				value: 'customTransition',
				label: 'Custom Transition',
				aliases: ['animate']
			},
			{
				category: 'Media',
				value: 'pause',
				label: 'Pause',
				aliases: ['stop']
			},
			{
				category: 'Media',
				value: 'play',
				label: 'Play',
				aliases: ['resume', 'start']
			},
			{
				category: 'Media',
				value: 'mute',
				label: 'Mute',
				aliases: ['silence']
			},
			{
				category: 'Media',
				value: 'unmute',
				label: 'Unmute',
				aliases: ['sound']
			},
			{
				category: 'Timer',
				value: 'start',
				label: 'Start',
				aliases: ['play', 'resume']
			},
			{
				category: 'Timer',
				value: 'stop',
				label: 'Stop',
				aliases: ['pause']
			},
			{
				category: 'Timer',
				value: 'reset',
				label: 'Reset',
				aliases: ['clear']
			},
			{
				category: 'Image',
				value: 'toggleImage',
				label: 'Toggle',
				aliases: ['trigger']
			},
			{
				category: 'Form',
				value: 'setValue',
				label: 'Set Value',
				aliases: ['update']
			},
			{
				category: 'Other',
				value: 'restoreInitialConfig',
				label: 'Restore initial values',
				aliases: ['Reset', 'Restore', 'Revert', 'Initial']
			}
		];
	}

	getPlayerStateOptions() {
		return [{
			label: this.translate.instant('IS_PLAYING'),
			value: 'isPlaying',
			type: 'boolean',
			aliases: [
				'Play state',
				'Paused'
			]
		},
		{
			label: this.translate.instant('CURRENT_TIME'),
			value: 'progress',
			type: 'number',
			aliases: [
				'Progress',
				'Playhead'
			]
		},
		{
			label: this.translate.instant('VOLUME'),
			value: 'volume',
			type: 'number',
			aliases: [
				'Sound'
			]
		},
		{
			label: this.translate.instant('IS_MUTED'),
			value: 'muted',
			type: 'boolean',
			aliases: [
				'Volume',
				'Sound'
			]
		},
		{
			label: this.translate.instant('IS_FULLSCREEN'),
			value: 'isFullscreen',
			type: 'boolean',
			aliases: [
				'Fullscreen',
				'Expanded'
			]
		},
		{
			label: this.translate.instant('CURRENT_SUBTITLE_LANGUAGE'),
			value: 'currentSubtitleLanguage',
			type: 'string',
			aliases: [
				'Subs',
				'Language'
			]
		},
		{
			label: this.translate.instant('CURRENT_SUBTITLE_ISO_CODE'),
			value: 'currentSubtitleIsoCode',
			type: 'string',
			aliases: [
				'Subs',
				'Language'
			]
		},
		{
			label: this.translate.instant('CURRENT_AUDIOTRACK_LANGUAGE'),
			value: 'currentAudioTrackLanguage',
			type: 'string',
			aliases: [
			]
		}];
		/**,
		{
			label: this.translate.instant('xxxxxx'),
			value: 'hasFacebookSharing',
			type: 'string',
			aliases: [
				'xxxxxx'
			]
		},
		{
			label: this.translate.instant('xxxxxx'),
			value: 'hasEmailSharing',
			type: 'string',
			aliases: [
				'xxxxxx'
			]
		},
		{
			label: this.translate.instant('xxxxxx'),
			value: 'hasTwitterSharing',
			type: 'string',
			aliases: [
				'xxxxxx'
			]
		},
		{
			label: this.translate.instant('xxxxxx'),
			value: 'hasLinkedinSharing',
			type: 'string',
			aliases: [
				'xxxxxx'
			]
		},
		{
			label: this.translate.instant('xxxxxx'),
			value: 'hasPinterestSharing',
			type: 'string',
			aliases: [
				'xxxxxx'
			]
		},
		{
			label: this.translate.instant('xxxxxx'),
			value: 'hasWhatsappSharing',
			type: 'string',
			aliases: [
				'xxxxxx'
			]
		},
		{
			label: this.translate.instant('xxxxxx'),
			value: 'browserLanguage',
			type: 'string',
			aliases: [
				'xxxxxx'
			]
		}, */

		/**
			value: 'device_isMobileDevice',
			value: 'device_measuredBandwidth',
			value: 'device_objectFit',
			value: 'device_hasWebGL',
			value: 'device_inIframe',
			value: 'device_isUIWebView',
			value: 'device_isWindowsPhone',
			value: 'device_isMobileDevice',
			value: 'device_isiPad',
			value: 'device_iPadVersion',
			value: 'device_iPadVersionCheck',
			value: 'device_isiPhone',
			value: 'device_iOSVersion',
			value: 'device_isiOS',
			value: 'device_isAndroid',
			value: 'device_isAndroidNative',
			value: 'device_androidVersion',
			value: 'device_canControlVolume',
			value: 'device_canPlayInline',
			value: 'device_devicePixelRatio',
			value: 'device_deviceResolution',
			value: 'device_deviceResolutionString',
			value: 'device_deviceOrientation',
			value: 'device_isIE',
			value: 'device_ieVersion',
			value: 'device_isEdge',
			value: 'device_edgeVersion',
			value: 'device_supportsHTML5IMA',
			value: 'device_isChrome',
			value: 'device_isFirefox',
			value: 'device_isWebkit',
			value: 'device_isGecko',
			value: 'device_isOpera',
			value: 'device_hasTouch',
			value: 'device_isTouchDevice',
			value: 'device_canAutoPlayAdvanced',
			value: 'device_svg',
		 */
	}

	makeCategoryMap() {
		const categoryMap = {
			player: {
				shortText: 'Control <strong class="entity">player</strong>',
				play: {
					closedText: {
						prop: '',
						val: 'radio'
					}
				},
				pause: {
					closedText: {
						prop: '',
						val: 'radio'
					}
				},
				volume: {
					closedText: {
						prop: 'radio',
						command: 'volume',
						val: 'param_0'
					},
					parameters: [{
						name: 'p_0',
						value: 1
					}],
				},
				mute: {},
				unmute: {},
				programVolume: {
					closedText: {
						prop: 'radio',
						command: 'volume',
						val: 'param_0'
					},
					parameters: [{
						name: 'p_0',
						value: 1
					}],
				},
				programMute: {},
				programUnmute: {},
				seek: {
					actionCat: 'navigate',
					shortText: 'Navigate to <strong class="entity">time-code</strong>',
					closedText: {
						prop: '',
						method: (param_0: any) => this.timeService.secondsToTimeString(param_0),
						val: 'param_0'
					},
				},
				seekchapter: {
					actionCat: 'navigate',
					shortText: 'Navigate to <strong class="entity">chapter</strong>',
					closedText: {
						prop: '',
						val: 'param_0'
					},
				},
				load: {
					actionCat: 'navigate',
					shortText: 'Navigate to <strong class="entity">clip</strong>',
					closedText: {
						prop: '',
						val: 'param_0;param_1'
					},
				},
				loadProject: {
					actionCat: 'navigate',
					shortText: 'Navigate to <strong class="entity">project</strong>',
					closedText: {
						prop: '',
						val: 'param_0;param_1'
					},
				},
				fullscreen: {
					closedText: {
						prop: '',
						val: 'radio'
					}
				},
				retractFullscreen: {
					closedText: {
						prop: 'subCat',
						val: 'radio'
					}
				},
				show: {
					closedText: {
						prop: 'param_0',
						command: '',
						val: 'radio'
					}
				},
				hide: {
					closedText: {
						prop: 'param_0',
						command: '',
						val: 'radio'
					}
				},
				enable: {
					closedText: {
						prop: 'param_0',
						command: '',
						val: 'radio'
					}
				},
				disable: {
					closedText: {
						prop: 'param_0',
						command: '',
						val: 'radio'
					}
				},
				hidesubtitlelayer: {
					closedText: {
						prop: 'param_0',
						command: 'subtitles',
						val: 'radio'
					}
				},
				showsubtitlelayer: {
					closedText: {
						prop: 'param_0',
						command: 'subtitles',
						val: 'radio'
					}
				},
				skipAd: {
					closedText: {
						prop: 'Player command',
						val: 'command'
					}
				},
				setsubtitle: {
					closedText: {
						prop: 'radio',
						command: '',
						val: 'param_0'
					}
				},
				setaudiotrackbyiso: {
					closedText: {
						prop: 'subCat',
						val: 'param_0'
					}
				},
				setaudiotrack: {},
				togglefullscreen: {
					closedText: {
						prop: 'subCat',
						val: 'radio'
					}
				},
				sharebyemail: {
					closedText: {
						prop: 'subCat',
						val: 'command'
					}
				},
				shareonfacebook: {
					closedText: {
						prop: 'subCat',
						val: 'command'
					}
				},
				shareontwitter: {
					closedText: {
						prop: 'subCat',
						val: 'command'
					}
				},
				shareonlinkedin: {
					closedText: {
						prop: 'subCat',
						val: 'command'
					}
				},
				shareonpinterest: {
					closedText: {
						prop: 'subCat',
						val: 'command'
					}
				},
				shareonwhatsapp: {
					closedText: {
						prop: 'subCat',
						val: 'command'
					}
				}
			},
			javascript: {
				shortText: 'Run <strong class="entity">javascript</strong>',
				__NONE: {
					closedText: {
						prop: '',
						val: 'param_0'
					},
					actionCat: 'script',
				}
			},
			navigation: {
				shortText: 'Navigate to <strong class="entity">webpage</strong>',
				__NONE: {
					closedText: {
						prop: '',
						val: 'param_0'
					},
					actionCat: 'navigate',
				}
			},
			download: {
				shortText: 'Navigate to <strong class="entity">downloadable file</strong>',
				__NONE: {
					closedText: {
						prop: '',
						val: 'param_0'
					},
					actionCat: 'navigate',
				}
			},
			widget: {
				show: {
					actionCat: 'widget'
				},
				hide: {
					actionCat: 'widget'
				},
				transition: {
					actionCat: 'widget'
				},
				setProperty: {
					actionCat: 'widget',
					closedText: {
						prop: 'param_1',
						val: 'param_2'
					}
				},
				callMethod: {
					actionCat: 'widget',
					closedText: {
						prop: 'param_1',
						method: (paramValue: any, action: IAction) => {
							if (action?.parameters?.[1]?.value === 'seek') {
								return this.timeService.secondsToTimeString(paramValue);
							} else {
								return paramValue;
							}
						},
						val: 'param_2'
					}
				},
				customTransition: {
					actionCat: 'widget'
				},
				restoreInitialConfig: {
					actionCat: 'widget'
				}
			},
			group: {
				show: {
					actionCat: 'widget'
				},
				hide: {
					actionCat: 'widget'
				},
				transition: {
					actionCat: 'widget'
				},
				restoreInitialConfig: {
					actionCat: 'widget'
				},
				setProperty: {
					actionCat: 'widget',
					closedText: {
						prop: 'param_1',
						val: 'param_2'
					}
				},
				callMethod: {
					actionCat: 'widget',
					closedText: {
						prop: 'param_1',
						val: 'param_2'
					}
				}
			},
			localstorage: {
				shortText: 'set <strong class="entity">local storage</strong>',
				setItemWithWidgetProperty: {
					shortText: 'set <strong class="entity">local storage</strong> from form field',
					closedText: {
						prop: 'param_0',
						val: 'param_1;param_2'
					}
				},
				setItem: {
					closedText: {
						prop: 'param_0',
						val: 'param_1'
					}
				},
				removeItem: {
					closedText: {
						prop: 'param_0',
						val: 'radio'
					}
				},
				clear: {}
			},
			message: {
				shortText: 'Send <strong class="entity">message</strong>',
				__NONE: {
					actionCat: 'send',
					closedText: {
						prop: '',
						val: 'command'
					},
				},
				__ANY: {
					actionCat: 'send',
					closedText: {
						prop: '',
						val: 'command'
					},
				}
			},
			analytics: {
				shortText: 'Log <strong class="entity">custom stats event</strong>',
				logevent: {
					actionCat: 'send'
				},
				closedText: {
					prop: 'param_0',
					val: 'param_1'
				}
			},
			form: {
				shortText: 'Control <strong class="entity">form</strong>',
				submit: {
					actionCat: 'send'
				},
				closedText: {
					prop: 'Form',
					val: 'Submit'
				}
			}
		};

		return categoryMap;
	}

	private dispatchChanges(): void {
		this._actionDropsitesSubject.next(this._actionDropsites);
	}

	private flattenConfig_recursive(config: any, prefix: string = '', result: any = {}): any {
		for (const key in config) {
			if (config.hasOwnProperty(key)) {
				if ((prefix + key).match(/^textFormat/i) && typeof config[key] === 'object' && config[key] !== null) { // NB!
					this.flattenConfig_recursive(config[key], prefix + key + '/', result);
				} else if ((prefix + key).match(/^validation/i) && typeof config[key] === 'object' && config[key] !== null) { // NB!
					this.flattenConfig_recursive(config[key], prefix + key + '/', result);
				} else { // non-textFormat or primitive or null
					result[prefix + key] = config[key];
				}
			}
		}

		return result;
	}
}
